import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import Input from "../../components/Input";
import { SelectSingle } from "../../components/SelectSingle";
import { MilitarSelectField } from "../../components/MilitarSelectField";
import { Plus, Trash2, Edit, Award } from "lucide-react";
import honrariasService, { Honraria, MilitarHonraria, AtribuirHonrariaData } from "../../services/honrariasService";
import { usePermissions } from "../../hooks/usePermissions";
import { enqueueSnackbar } from "notistack";

export function Honrarias() {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState<"honrarias" | "atribuir">("honrarias");
  const [honrarias, setHonrarias] = useState<Honraria[]>([]);
  const [militarHonrarias, setMilitarHonrarias] = useState<MilitarHonraria[]>([]);
  const [loading, setLoading] = useState(false);

  // Form de criar/editar honraria
  const [editingHonraria, setEditingHonraria] = useState<Honraria | null>(null);
  const [nomeHonraria, setNomeHonraria] = useState("");
  const [imgHonraria, setImgHonraria] = useState("");

  // Form de atribuir honraria
  const [selectedMilitar, setSelectedMilitar] = useState("");
  const [selectedHonraria, setSelectedHonraria] = useState("");
  const [selectedTipo, setSelectedTipo] = useState<"Grã Cruz" | "Comendador" | "Cavaleiro">("Cavaleiro");

  const tiposHonraria = [
    { value: "Cavaleiro", label: "Cavaleiro" },
    { value: "Comendador", label: "Comendador" },
    { value: "Grã Cruz", label: "Grã Cruz" },
  ];

  useEffect(() => {
    loadHonrarias();
    loadMilitarHonrarias();
  }, []);

  async function loadHonrarias() {
    try {
      setLoading(true);
      const data = await honrariasService.listHonrarias();
      setHonrarias(data);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao carregar honrarias", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }

  async function loadMilitarHonrarias() {
    try {
      const data = await honrariasService.listMilitarHonrarias();
      setMilitarHonrarias(data);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao carregar honrarias dos militares", { variant: "error" });
    }
  }

  async function handleSaveHonraria() {
    try {
      if (!nomeHonraria || !imgHonraria) {
        enqueueSnackbar("Preencha todos os campos", { variant: "warning" });
        return;
      }

      setLoading(true);

      if (editingHonraria) {
        await honrariasService.updateHonraria(editingHonraria.id, {
          honraria: nomeHonraria,
          img: imgHonraria,
        });
        enqueueSnackbar("Honraria atualizada com sucesso", { variant: "success" });
      } else {
        await honrariasService.createHonraria({
          honraria: nomeHonraria,
          img: imgHonraria,
        });
        enqueueSnackbar("Honraria criada com sucesso", { variant: "success" });
      }

      setNomeHonraria("");
      setImgHonraria("");
      setEditingHonraria(null);
      loadHonrarias();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao salvar honraria", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteHonraria(id: string) {
    if (!window.confirm("Tem certeza que deseja excluir esta honraria?")) return;

    try {
      setLoading(true);
      await honrariasService.deleteHonraria(id);
      enqueueSnackbar("Honraria excluída com sucesso", { variant: "success" });
      loadHonrarias();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao excluir honraria", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }

  function handleEditHonraria(honraria: Honraria) {
    setEditingHonraria(honraria);
    setNomeHonraria(honraria.honraria);
    setImgHonraria(honraria.img);
  }

  function handleCancelEdit() {
    setEditingHonraria(null);
    setNomeHonraria("");
    setImgHonraria("");
  }

  async function handleAtribuirHonraria() {
    try {
      if (!selectedMilitar || !selectedHonraria) {
        enqueueSnackbar("Selecione um militar e uma honraria", { variant: "warning" });
        return;
      }

      setLoading(true);

      const data: AtribuirHonrariaData = {
        idMilitar: selectedMilitar,
        idHonraria: selectedHonraria,
        tipo: selectedTipo,
      };

      await honrariasService.atribuirHonraria(data);
      enqueueSnackbar("Honraria atribuída com sucesso", { variant: "success" });

      setSelectedMilitar("");
      setSelectedHonraria("");
      setSelectedTipo("Cavaleiro");
      loadMilitarHonrarias();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao atribuir honraria", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }

  async function handleRemoverHonraria(id: string) {
    if (!window.confirm("Tem certeza que deseja remover esta honraria do militar?")) return;

    try {
      setLoading(false);
      await honrariasService.removerHonraria(id);
      enqueueSnackbar("Honraria removida com sucesso", { variant: "success" });
      loadMilitarHonrarias();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao remover honraria", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Honrarias</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "honrarias"
              ? "border-b-2 border-primary text-primary"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("honrarias")}
        >
          Honrarias
        </button>
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "atribuir"
              ? "border-b-2 border-primary text-primary"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("atribuir")}
        >
          Atribuir Honrarias
        </button>
      </div>

      {/* Tab: Honrarias */}
      {activeTab === "honrarias" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Formulário */}
          {hasPermission("criar_honraria") && (
            <Card>
              <CardHeader>
                <CardTitle>{editingHonraria ? "Editar Honraria" : "Nova Honraria"}</CardTitle>
                <CardDescription>
                  {editingHonraria ? "Atualize os dados da honraria" : "Crie uma nova honraria"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  title="Nome da Honraria"
                  value={nomeHonraria}
                  onChange={(e) => setNomeHonraria(e.target.value)}
                  placeholder="Ex: Ordem do Mérito"
                />
                <Input
                  title="URL da Imagem"
                  value={imgHonraria}
                  onChange={(e) => setImgHonraria(e.target.value)}
                  placeholder="https://..."
                />
                <div className="flex gap-2">
                  <Button onClick={handleSaveHonraria} disabled={loading} className="flex-1">
                    <Plus className="w-4 h-4 mr-2" />
                    {editingHonraria ? "Atualizar" : "Criar"}
                  </Button>
                  {editingHonraria && (
                    <Button onClick={handleCancelEdit} variant="outline">
                      Cancelar
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lista de Honrarias */}
          <Card>
            <CardHeader>
              <CardTitle>Honrarias Cadastradas</CardTitle>
              <CardDescription>Lista de todas as honrarias do sistema</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {honrarias.map((honraria) => (
                  <div
                    key={honraria.id}
                    className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={honraria.img}
                        alt={honraria.honraria}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <span className="font-medium">{honraria.honraria}</span>
                    </div>
                    <div className="flex gap-2">
                      {hasPermission("editar_honraria") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditHonraria(honraria)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                      {hasPermission("excluir_honraria") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteHonraria(honraria.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab: Atribuir Honrarias */}
      {activeTab === "atribuir" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Formulário de Atribuição */}
          {hasPermission("atribuir_honraria") && (
            <Card>
              <CardHeader>
                <CardTitle>Atribuir Honraria</CardTitle>
                <CardDescription>Conceda uma honraria a um militar</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <MilitarSelectField
                  label="Militar"
                  value={selectedMilitar}
                  onChange={setSelectedMilitar}
                />
                <SelectSingle
                  title="Honraria"
                  name="honraria"
                  value={selectedHonraria}
                  onChange={(e) => setSelectedHonraria(e.target.value)}
                >
                  <option value="">Selecione uma honraria</option>
                  {honrarias.map((h) => (
                    <option key={h.id} value={h.id}>{h.honraria}</option>
                  ))}
                </SelectSingle>
                <SelectSingle
                  title="Tipo"
                  name="tipo"
                  value={selectedTipo}
                  onChange={(e) => setSelectedTipo(e.target.value as any)}
                >
                  <option value="Cavaleiro">Cavaleiro</option>
                  <option value="Comendador">Comendador</option>
                  <option value="Grã Cruz">Grã Cruz</option>
                </SelectSingle>
                <Button onClick={handleAtribuirHonraria} disabled={loading} className="w-full">
                  <Award className="w-4 h-4 mr-2" />
                  Atribuir Honraria
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Lista de Honrarias Atribuídas */}
          <Card>
            <CardHeader>
              <CardTitle>Honrarias Atribuídas</CardTitle>
              <CardDescription>Militares que possuem honrarias</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {militarHonrarias.map((mh) => (
                  <div
                    key={mh.id}
                    className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={mh.honraria.img}
                        alt={mh.honraria.honraria}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-medium">
                          {mh.militar.patente.abreviacao} {mh.militar.nick}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {mh.honraria.honraria} - {mh.tipo}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Por: {mh.militarPromoveu.patente.abreviacao} {mh.militarPromoveu.nick}
                        </div>
                      </div>
                    </div>
                    {hasPermission("remover_honraria") && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoverHonraria(mh.id)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
